import { EventName } from "module/analytics/types"
import { formatNumberPositionSize, formatNumberUSD } from "util/format"

import {
    Badge,
    Box,
    Center,
    HStack,
    Spinner,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tooltip,
    Tr,
    VStack,
    useToast,
} from "@chakra-ui/react"
import { PositionSide } from "@perp/sdk-curie"
import { Countdown } from "component/Countdown"
import { MarketListIcon } from "component/MarketListIcon"
import { PerpIconButton } from "component/PerpIconButton"
import { TextNote } from "component/Typography"
import { ActiveLimitOrdersContainer } from "container/service/LimitOrderServiceContainer/useActiveLimitOrder"
import { useBaseAssetSymbolFromRouterParams, useTickerSymbolFromRouterParams } from "hook/useRouterParams"
import { useCallback } from "react"
import { useTranslation } from "react-i18next"
import { RiDeleteBin7Fill } from "react-icons/ri"
import { MIN_ORDER_VALUE_IN_USD } from "sdk-react/limitOrder/constants"
import { OrderType } from "sdk-react/limitOrder/types"
import { useCancelLimitOrder } from "sdk-react/limitOrder/useCancelLimitOrder"
import { ActiveLimitOrder, ActiveLimitOrderErrorCode } from "service/appSync/LimitOrderService"

const PositionBadge = ({ isLong }: { isLong: boolean }) => {
    const { t } = useTranslation()

    return (
        <Badge variant="subtle" colorScheme={isLong ? "green" : "red"}>
            {isLong ? t("position.side_long") : t("position.side_short")}
        </Badge>
    )
}

interface ActiveOrderListRowProps {
    order: ActiveLimitOrder
}

function ActiveOrderListRow({ order }: ActiveOrderListRowProps) {
    const { orderType, positionSide, positionSize, price, triggerPrice, reduceOnly, hash, deadline, errorCode } = order
    const baseSymbol = useBaseAssetSymbolFromRouterParams()
    const { t } = useTranslation()
    const { cancelLimitOrder } = useCancelLimitOrder()

    const handleClick = useCallback(async () => {
        return cancelLimitOrder(order.normalizedData)
    }, [cancelLimitOrder, order])

    const showTriggerPrice = orderType === OrderType.STOP_LIMIT || orderType === OrderType.TAKE_PROFIT_LIMIT

    const orderTypeTextMap: Record<OrderType, string> = {
        [OrderType.MARKET]: t("general.order_type_market"),
        [OrderType.LIMIT]: t("general.order_type_limit"),
        [OrderType.STOP_LIMIT]: t("general.order_type_stop_limit"),
        [OrderType.TAKE_PROFIT_LIMIT]: t("general.order_type_take_profit_limit"),
    }

    const errorTextMap: Record<ActiveLimitOrderErrorCode, string> = {
        [ActiveLimitOrderErrorCode.LOB_ROINS]: t("notification.error_reduce_only_is_not_satisfied"),
        [ActiveLimitOrderErrorCode.LOB_OVTS]: t("notification.error_order_value_too_small", {
            amount: MIN_ORDER_VALUE_IN_USD,
        }),
        [ActiveLimitOrderErrorCode.CH_NEFCI]: t("notification.error_not_enough_free_collateral"),
        [ActiveLimitOrderErrorCode.AB_MNE]: t("notification.error_market_number_exceeds"),

        [ActiveLimitOrderErrorCode.LOB_SSLOTPNM]: t("notification.trigger_price_not_met_by_index_price"),
        [ActiveLimitOrderErrorCode.LOB_BSLOTPNM]: t("notification.trigger_price_not_met_by_index_price"),
        [ActiveLimitOrderErrorCode.LOB_STLOTPNM]: t("notification.trigger_price_not_met_by_index_price"),
        [ActiveLimitOrderErrorCode.LOB_BTLOTPNM]: t("notification.trigger_price_not_met_by_index_price"),

        [ActiveLimitOrderErrorCode.CH_TLRS]: t("notification.limit_price_not_met_by_market_price"),
        [ActiveLimitOrderErrorCode.CH_TMRS]: t("notification.limit_price_not_met_by_market_price"),
        [ActiveLimitOrderErrorCode.CH_TLRL]: t("notification.limit_price_not_met_by_market_price"),
        [ActiveLimitOrderErrorCode.CH_TMRL]: t("notification.limit_price_not_met_by_market_price"),
    }
    const errorText = errorTextMap[errorCode]

    const toast = useToast()

    return (
        <Tr>
            <Td>
                <Tooltip hasArrow label={"Cancelling orders requires gas fees."}>
                    <Box>
                        <PerpIconButton
                            icon={<RiDeleteBin7Fill size={16} />}
                            size="xs"
                            onClick={handleClick}
                            colorScheme="red"
                            trackingProps={{ eventName: EventName.LIMIT_ORDER_CANCEL_CLICK }}
                            aria-label={"Cancel order"}
                        />
                    </Box>
                </Tooltip>
            </Td>
            <Td>
                <HStack>
                    <Box boxSize="16px">
                        <MarketListIcon coin={baseSymbol} boxSize="16px" />
                    </Box>
                    <Text>{baseSymbol}</Text>
                    <PositionBadge isLong={positionSide === PositionSide.LONG} />
                </HStack>
            </Td>
            <Td>{formatNumberPositionSize(positionSize)}</Td>
            <Td>
                <VStack align="stretch">
                    {showTriggerPrice && (
                        <HStack>
                            <Text>{formatNumberUSD({ num: triggerPrice })}</Text>
                            <TextNote>{"Trigger"}</TextNote>
                        </HStack>
                    )}
                    <HStack>
                        <Text>{formatNumberUSD({ num: price })}</Text>
                        {showTriggerPrice && <TextNote>{"Limit"}</TextNote>}
                    </HStack>
                </VStack>
            </Td>
            <Td>
                <Text>
                    {orderTypeTextMap[orderType]}
                    <Text color="gray.400" as="span">
                        {reduceOnly && " (Reduce-only)"}
                    </Text>
                </Text>
            </Td>
            <Td>
                <VStack whiteSpace={"normal"} align="stretch" width={"200px"} spacing={0}>
                    {errorText && <Text mb={"2px"}>{errorText}</Text>}
                    <Countdown
                        deadline={deadline}
                        onClick={() => {
                            toast({
                                title: "Order hash copied.",
                                status: "info",
                                duration: 1000,
                                isClosable: true,
                            })
                            navigator.clipboard.writeText(hash)
                        }}
                        lineHeight={1.3}
                        fontWeight="normal"
                        fontSize={"xs"}
                    />
                </VStack>
            </Td>
        </Tr>
    )
}

export function ActiveLimitOrderList() {
    const tickerSymbol = useTickerSymbolFromRouterParams()
    const { isLoading, activeLimitOrdersMap } = ActiveLimitOrdersContainer.useContainer()
    const { t } = useTranslation()

    if (isLoading)
        return (
            <Center minH="190px">
                <Spinner />
            </Center>
        )

    const orders = activeLimitOrdersMap[tickerSymbol] ?? []
    const isEmpty = orders.length === 0
    if (isEmpty) {
        return (
            <Center h="200px">
                <VStack w="300px" textAlign="center">
                    <Text color="whiteAlpha.500" fontSize="md">
                        You don't have any active orders yet.
                    </Text>
                </VStack>
            </Center>
        )
    }

    return (
        <Table variant="simple" size="sm" width="100%" sx={{ whiteSpace: "nowrap" }}>
            <Thead>
                <Tr>
                    <Th></Th>
                    <Th>{t("position.market_side")}</Th>
                    <Th>{t("position.position_size")}</Th>
                    <Th>{t("general.price")}</Th>
                    <Th>{t("general.order_type")}</Th>
                    <Th>{t("general.status")}</Th>
                </Tr>
            </Thead>
            <Tbody>
                {orders.map(order => {
                    return <ActiveOrderListRow order={order} key={order.hash} />
                })}
            </Tbody>
        </Table>
    )
}
